import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, LogOut, KeyRound, Menu, X, Sun, Moon, LayoutGrid, Briefcase, Settings, ShieldAlert, Map } from "lucide-react";
import { useEffect, useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { getInitialTheme, applyTheme } from "../lib/theme";
import { PLATFORM_COMPANY_NAME } from "../lib/platformCompany";
import ChangePasswordDialog from "./ChangePasswordDialog";
import api from "../lib/api";

// Consolidação Comercial (item 5) — defesa em profundidade contra texto de
// comentário de env vazado como dado real (a correção de fato é a migração
// backend + reformatação de env/.env.*.example, ver server.py). Nunca
// renderiza um nome/e-mail que comece com "#" (nenhum nome/e-mail real
// legítimo começa assim) — mostra um rótulo genérico em vez de expor o
// texto cru na interface.
const safeDisplayText = (value, fallback) => {
  const v = (value || "").trim();
  return v && !v.startsWith("#") ? v : fallback;
};

// Layout exclusivo do admin_master — administrador da plataforma LOOTUS, não
// de uma empresa cliente. Deliberadamente não reaproveita o Layout
// operacional (Dashboard/Clientes/Fichas/...): não faz sentido para quem não
// pertence a um tenant, e mostrar esse menu seria sugerir acesso operacional
// que o Admin Master não tem. Navegação simples por enquanto: só
// Administração → Empresas.
// Experimento visual da sidebar (delta 2026-09-16, referência visual
// fornecida) — `section` é só um rótulo de agrupamento visual (mesmos
// itens/rotas/permissões de sempre, exclusivos do admin_master via
// ProtectedRoute em App.js, não tocado por este delta).
const NAV_SECTION_PLATAFORMA = "Plataforma";
const NAV_SECTION_ADMINISTRACAO = "Administração";
const nav = [
  { to: "/admin/empresas", label: "Empresas", icon: Building2, section: NAV_SECTION_PLATAFORMA },
  // Planos/Recursos (Fase 1 — Fundação Comercial, item 20) — área
  // provisória: Planos, Recursos e a Matriz Plano×Recurso.
  { to: "/admin/planos", label: "Planos e Recursos", icon: LayoutGrid, section: NAV_SECTION_PLATAFORMA },
  // Gestão Comercial SaaS — cobrança da LOOTUS às empresas clientes
  // (domínio separado do faturamento operacional de cada tenant).
  { to: "/admin/comercial", label: "Gestão Comercial", icon: Briefcase, section: NAV_SECTION_PLATAFORMA },
  // Ajuste dos E-mails da Plataforma — área geral (item 6 do pedido): o
  // financeiro continua em Gestão Comercial (já homologado), o perfil de
  // sistema/segurança é novo e vive aqui.
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings, section: NAV_SECTION_ADMINISTRACAO },
  // Monitor Fiscal / Regulatório (ver docs/MONITOR_FISCAL.md) — só alerta,
  // nunca altera código/XSD/integração fiscal sozinho.
  { to: "/admin/regulatorio", label: "Monitor Fiscal", icon: ShieldAlert, section: NAV_SECTION_ADMINISTRACAO },
  // Roadmap (item 1 do pedido "IMPLEMENTAR ROADMAP MASTER") — acompanhamento
  // da evolução do LOOTUS, exclusivo do Ambiente Master (mesma proteção
  // require_role("admin_master") das demais rotas /admin/* acima).
  { to: "/admin/roadmap", label: "Roadmap", icon: Map, section: NAV_SECTION_ADMINISTRACAO },
];
const NAV_SECTIONS_ORDER = [NAV_SECTION_PLATAFORMA, NAV_SECTION_ADMINISTRACAO];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  // Tema Claro/Escuro (Correções, item 4) — mesma preferência de usuário do
  // Layout operacional, disponível também para o admin_master.
  const [theme, setTheme] = useState(getInitialTheme);
  useEffect(() => { applyTheme(theme); }, [theme]);
  const toggleTheme = () => setTheme(t => (t === "dark" ? "light" : "dark"));

  // Badge "Monitor Fiscal (N)" — só conta alertas CRÍTICOS/ALTOS em aberto
  // (item 13 do pedido). Falha em buscar nunca quebra o resto do layout.
  const [regulatoryOpenCount, setRegulatoryOpenCount] = useState(0);
  useEffect(() => {
    let active = true;
    api.get("/admin/regulatory/summary").then(({ data }) => {
      if (!active) return;
      const n = (data?.counts?.CRITICAL || 0) + (data?.counts?.HIGH || 0);
      setRegulatoryOpenCount(n);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  // Agrupamento puramente visual dos MESMOS itens de `nav` acima —
  // nenhuma rota/permissão nova (todas já exclusivas de admin_master via
  // ProtectedRoute em App.js, não tocado por este delta).
  const navGroups = NAV_SECTIONS_ORDER
    .map(section => ({ section, items: nav.filter(i => i.section === section) }))
    .filter(g => g.items.length > 0);
  const navUngrouped = nav.filter(i => !NAV_SECTIONS_ORDER.includes(i.section));
  const renderNavItem = ({ to, label, icon: Icon }, onClick) => (
    <NavLink key={to} to={to} onClick={onClick} data-testid={`admin-nav-${label.toLowerCase()}`}
      className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] text-slate-300 ${isActive ? "active" : ""}`}>
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {label === "Monitor Fiscal" && regulatoryOpenCount > 0 && (
        <span className="text-[11px] font-semibold bg-red-500/20 text-red-300 rounded-full px-2 py-0.5">
          {regulatoryOpenCount}
        </span>
      )}
    </NavLink>
  );
  const renderNavGroups = (onClick) => (
    <>
      {navGroups.map(({ section, items }) => (
        <div key={section}>
          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{section}</div>
          <div className="space-y-1">{items.map(item => renderNavItem(item, onClick))}</div>
        </div>
      ))}
      {navUngrouped.map(item => renderNavItem(item, onClick))}
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar Admin Master — experimento visual (delta 2026-09-16,
          referência visual fornecida): mesma linguagem escura/cápsula do
          menu do tenant, exclusiva deste layout (nunca compartilhada com
          Layout.jsx — cada um continua com seu próprio menu).
          Correção de rolagem vertical (delta 2026-09-18) — mesma causa
          raiz e mesma correção do menu do tenant (ver Layout.jsx):
          container pai `min-h-screen flex` só garante um piso de altura,
          então em página longa o <aside> esticava junto (align-items:
          stretch padrão, sem altura própria). `h-screen` trava em 100vh;
          `sticky top-0 self-start` prende o menu no topo durante a
          rolagem da janela. O <nav> abaixo já era `flex-1
          overflow-y-auto` — passa a rolar internamente de verdade quando
          os itens não cabem, sem empurrar o rodapé para fora. */}
      <aside className="hidden md:flex md:w-[260px] md:h-screen md:sticky md:top-0 md:self-start flex-col bg-gradient-to-b from-slate-950 to-slate-900 border-r border-slate-800/70">
        <div className="h-16 flex items-center px-6 border-b border-slate-800/70">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-heading font-black" style={{ background: "linear-gradient(135deg, #0F172A, #334155)" }}>L</div>
          <div className="ml-3">
            <div className="font-heading font-black text-white leading-tight text-[16px] tracking-tight">LOOTUS</div>
            <div className="text-[10px] tracking-[0.2em] text-slate-400 leading-tight">ADMINISTRAÇÃO</div>
          </div>
        </div>
        <nav className="p-3 flex-1 space-y-4 overflow-y-auto" data-testid="admin-sidebar-nav">
          {renderNavGroups()}
        </nav>
        <div className="p-4 text-xs text-slate-500 border-t border-slate-800/70">
          Powered by <b className="text-blue-400">{PLATFORM_COMPANY_NAME}</b>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-950 to-slate-900 border-r border-slate-800/70 p-3 overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-3">
              <div className="font-heading font-bold text-white">Administração</div>
              <button data-testid="admin-sidebar-close" className="text-slate-300" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="space-y-4">{renderNavGroups(() => setOpen(false))}</div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6">
          <button data-testid="admin-sidebar-open" className="md:hidden p-2" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="md:hidden font-heading font-bold">Administração</div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="admin-theme-toggle" title={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}>
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="admin-user-menu" className="flex items-center gap-3 hover:bg-slate-100 rounded-md px-2 py-1.5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-heading font-semibold">
                    {safeDisplayText(user?.name, "Administrador da Plataforma").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium leading-tight">{safeDisplayText(user?.name, "Administrador da Plataforma")}</div>
                    <div className="text-xs text-slate-500 leading-tight">Admin Master</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{safeDisplayText(user?.email, "")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="admin-menu-change-password" onClick={() => setPwOpen(true)}>
                  <KeyRound size={16} /> Alterar senha
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="admin-menu-logout" onClick={doLogout}>
                  <LogOut size={16} /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 md:p-8 flex-1 fade-in">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
